/**
 * Guest write tools — create, update and (soft) delete guests by reusing the
 * existing Firestore helpers in features/guests/operations.ts.
 */

import { Schema } from "firebase/ai";
import { GuestSchema } from "@amazing-wedding/shared-types";
import type { GuestDraft, GuestPatch } from "../operations";
import type { PlannerTool } from "./registry";
import { asBoolean, asEnum, asString, requireString } from "./helpers";

const RSVP_VALUES = ["pending", "accepted", "declined"] as const;
const MEAL_VALUES = [
  "standard",
  "vegetarian",
  "vegan",
  "gluten_free",
  "lactose_free",
  "child",
  "other",
] as const;

/** Cap on guests accepted by a single batch call — keeps one turn bounded. */
const MAX_BATCH = 50;

/** Validates an assembled draft with the shared Zod schema (minus server fields). */
const GuestDraftSchema = GuestSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  rsvpRespondedAt: true,
  rsvpToken: true,
  rsvpSubmittedToken: true,
});

function guestName(
  ctx: { guests: () => { id: string; firstName: string; lastName: string }[] },
  id: string,
): string {
  const g = ctx.guests().find((x) => x.id === id);
  return g ? `${g.firstName} ${g.lastName}`.trim() : id;
}

/**
 * Case- and accent-insensitive name normaliser for the duplicate-guest
 * check. `ł` doesn't decompose under NFD so we replace it explicitly;
 * everything else (ą, ć, ę, ń, ó, ś, ź, ż) decomposes cleanly.
 */
function normalizePolishName(s: string): string {
  return s
    .toLocaleLowerCase("pl-PL")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/ł/g, "l")
    .replace(/\s+/g, " ")
    .trim();
}

/** Returns the first existing guest whose normalised first+last matches. */
function findDuplicateGuest(
  guests: { id: string; firstName: string; lastName: string }[],
  firstName: string,
  lastName: string,
): { id: string; firstName: string; lastName: string } | null {
  const target = `${normalizePolishName(firstName)} ${normalizePolishName(lastName)}`;
  for (const g of guests) {
    const candidate = `${normalizePolishName(g.firstName)} ${normalizePolishName(g.lastName)}`;
    if (candidate === target) return g;
  }
  return null;
}

export { normalizePolishName, findDuplicateGuest };

/** Build (and validate) a GuestDraft from loosely-typed function-call args. */
function buildGuestDraft(args: Record<string, unknown>): GuestDraft {
  const draft: GuestDraft = {
    firstName: requireString(args, "firstName"),
    lastName: requireString(args, "lastName"),
    phone: asString(args.phone) ?? null,
    groupId: asString(args.groupId) ?? null,
    rsvpStatus: asEnum(args.rsvpStatus, RSVP_VALUES) ?? "pending",
    mealPreference: asEnum(args.mealPreference, MEAL_VALUES) ?? null,
    attendingCeremony: true,
    attendingReception: true,
    attendingAfterParty: false,
    accommodationRequired: false,
    tableId: null,
    seatNumber: null,
    plusOneAllowed: asBoolean(args.plusOneAllowed) ?? false,
    plusOne: null,
    children: [],
    notes: asString(args.notes) ?? null,
    importSource: "manual",
  };
  return GuestDraftSchema.parse(draft) as GuestDraft;
}

/** Shared schema for one guest in single- and batch-create tools. */
const GUEST_INPUT_PROPERTIES = {
  firstName: Schema.string({
    description:
      "Guest's first name in its canonical Polish form (Elżbieta, Katarzyna, Tomasz — not the diminutive).",
  }),
  lastName: Schema.string({ description: "Guest's last name." }),
  phone: Schema.string({ description: "Phone number (optional)." }),
  rsvpStatus: Schema.enumString({
    enum: [...RSVP_VALUES],
    description: "RSVP status. Defaults to 'pending'.",
  }),
  mealPreference: Schema.enumString({
    enum: [...MEAL_VALUES],
    description: "Meal preference (optional).",
  }),
  groupId: Schema.string({
    description: "ID of an existing guest group (from list_groups, optional).",
  }),
  plusOneAllowed: Schema.boolean({ description: "Whether this guest may bring a plus-one." }),
  notes: Schema.string({ description: "Free-text notes (optional)." }),
};
const GUEST_INPUT_OPTIONAL = [
  "phone",
  "rsvpStatus",
  "mealPreference",
  "groupId",
  "plusOneAllowed",
  "notes",
];

export const guestTools: PlannerTool[] = [
  {
    declaration: {
      name: "create_guest",
      description:
        "Add a new guest to the wedding guest list. Use when the user wants to invite or register someone. Pass the canonical Polish given name (e.g. Elżbieta, not the diminutive Ela). If a guest with the same first+last name already exists, the call fails with a duplicate error so you can confirm with the user before retrying with confirmDuplicate=true.",
      parameters: Schema.object({
        properties: {
          ...GUEST_INPUT_PROPERTIES,
          confirmDuplicate: Schema.boolean({
            description:
              "Pass true ONLY after the user has confirmed that adding another guest with an already-present first+last name is intentional.",
          }),
        },
        optionalProperties: [...GUEST_INPUT_OPTIONAL, "confirmDuplicate"],
      }),
    },
    destructive: false,
    describe: (args) =>
      `Dodanie gościa: ${asString(args.firstName) ?? "?"} ${asString(args.lastName) ?? ""}`.trim(),
    execute: async (args, ctx) => {
      const firstName = requireString(args, "firstName");
      const lastName = requireString(args, "lastName");
      const confirmDuplicate = asBoolean(args.confirmDuplicate) ?? false;
      if (!confirmDuplicate) {
        const dup = findDuplicateGuest(ctx.guests(), firstName, lastName);
        if (dup) {
          const existingName = `${dup.firstName} ${dup.lastName}`.trim();
          throw new Error(
            `Duplicate guest: "${existingName}" (id: ${dup.id}) is already on the list. Ask the user whether to add another person with the same name, and only then retry create_guest with confirmDuplicate=true. Do not retry blindly.`,
          );
        }
      }
      const draft = buildGuestDraft(args);
      const id = await ctx.operations.guests.createGuest(ctx.weddingId, draft);
      await ctx.operations.guests.syncAttendeeCount(ctx.weddingId, [
        ...ctx.guests(),
        ctx.operations.guests.projectCreatedGuest(id, draft),
      ]);
      return { ok: true, guestId: id, name: `${draft.firstName} ${draft.lastName}` };
    },
  },
  {
    declaration: {
      name: "create_guests_batch",
      description:
        "Add several guests to the list in a single call. STRONGLY PREFER this over multiple create_guest calls when the user wants to add more than one guest (e.g. from a photo, a pasted list, or a family) — it avoids exhausting the per-message tool-call budget. Each item in 'guests' takes the same per-guest fields as create_guest (firstName, lastName, phone, rsvpStatus, mealPreference, groupId, plusOneAllowed, notes); resolve Polish diminutives to canonical names first. NOTE: confirmDuplicate is a single top-level argument that applies to the whole batch — it is NOT a per-guest field. Guests whose first+last name already exists are skipped unless confirmDuplicate is true, and reported back so you can confirm with the user.",
      parameters: Schema.object({
        properties: {
          guests: Schema.array({
            description: `List of guests to create (max ${MAX_BATCH}).`,
            items: Schema.object({
              properties: { ...GUEST_INPUT_PROPERTIES },
              optionalProperties: [...GUEST_INPUT_OPTIONAL],
            }),
          }),
          confirmDuplicate: Schema.boolean({
            description:
              "Pass true ONLY after the user confirmed that adding guests whose first+last name already exists is intentional. Applies to the whole batch.",
          }),
        },
        optionalProperties: ["confirmDuplicate"],
      }),
    },
    destructive: false,
    describe: (args) => {
      const list = Array.isArray(args.guests) ? args.guests : [];
      return `Dodanie gości: ${list.length}`;
    },
    execute: async (args, ctx) => {
      const rawList = Array.isArray(args.guests) ? (args.guests as Record<string, unknown>[]) : [];
      if (rawList.length === 0) throw new Error("The 'guests' array is empty.");
      if (rawList.length > MAX_BATCH) {
        throw new Error(`Too many guests (${rawList.length}). Send at most ${MAX_BATCH} per call.`);
      }
      const confirmDuplicate = asBoolean(args.confirmDuplicate) ?? false;

      // Track names added in this batch too, so intra-batch dupes are caught.
      const seen = ctx.guests().map((g) => ({
        id: g.id,
        firstName: g.firstName,
        lastName: g.lastName,
      }));
      const created: { guestId: string; name: string; draft: GuestDraft }[] = [];
      const skippedDuplicates: { index: number; name: string; existingId: string }[] = [];
      const failed: { index: number; name: string | null; error: string }[] = [];

      for (let i = 0; i < rawList.length; i++) {
        const item = rawList[i] ?? {};
        try {
          const firstName = requireString(item, "firstName");
          const lastName = requireString(item, "lastName");
          if (!confirmDuplicate) {
            const dup = findDuplicateGuest(seen, firstName, lastName);
            if (dup) {
              skippedDuplicates.push({
                index: i,
                name: `${firstName} ${lastName}`.trim(),
                existingId: dup.id,
              });
              continue;
            }
          }
          const draft = buildGuestDraft(item);
          const id = await ctx.operations.guests.createGuest(ctx.weddingId, draft);
          seen.push({ id, firstName: draft.firstName, lastName: draft.lastName });
          created.push({ guestId: id, name: `${draft.firstName} ${draft.lastName}`.trim(), draft });
        } catch (err) {
          failed.push({
            index: i,
            name:
              `${asString(item.firstName) ?? ""} ${asString(item.lastName) ?? ""}`.trim() || null,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }

      // One attendee-count sync after all inserts (cheaper than per-guest).
      if (created.length > 0) {
        const projected = created.map((c) =>
          ctx.operations.guests.projectCreatedGuest(c.guestId, c.draft),
        );
        await ctx.operations.guests.syncAttendeeCount(ctx.weddingId, [
          ...ctx.guests(),
          ...projected,
        ]);
      }

      return {
        ok: failed.length === 0 && skippedDuplicates.length === 0,
        createdCount: created.length,
        created: created.map(({ guestId, name }) => ({ guestId, name })),
        skippedDuplicates,
        failed,
      };
    },
  },
  {
    declaration: {
      name: "update_guest",
      description:
        "Update an existing guest. You MUST first call list_guests to obtain the exact guestId — never invent an ID. Only the fields you pass are changed.",
      parameters: Schema.object({
        properties: {
          guestId: Schema.string({ description: "Exact guest ID from list_guests." }),
          firstName: Schema.string(),
          lastName: Schema.string(),
          phone: Schema.string(),
          rsvpStatus: Schema.enumString({ enum: [...RSVP_VALUES] }),
          mealPreference: Schema.enumString({ enum: [...MEAL_VALUES] }),
          groupId: Schema.string({ description: "Guest group ID from list_groups." }),
          accommodationRequired: Schema.boolean(),
          notes: Schema.string(),
        },
        optionalProperties: [
          "firstName",
          "lastName",
          "phone",
          "rsvpStatus",
          "mealPreference",
          "groupId",
          "accommodationRequired",
          "notes",
        ],
      }),
    },
    destructive: false,
    describe: (args, ctx) => `Aktualizacja gościa: ${guestName(ctx, asString(args.guestId) ?? "")}`,
    execute: async (args, ctx) => {
      const guestId = requireString(args, "guestId");
      if (!ctx.guests().some((g) => g.id === guestId)) {
        throw new Error(`No guest with ID "${guestId}". Call list_guests first.`);
      }
      const patch: GuestPatch = {};
      const firstName = asString(args.firstName);
      const lastName = asString(args.lastName);
      const phone = asString(args.phone);
      const rsvpStatus = asEnum(args.rsvpStatus, RSVP_VALUES);
      const mealPreference = asEnum(args.mealPreference, MEAL_VALUES);
      const groupId = asString(args.groupId);
      const accommodationRequired = asBoolean(args.accommodationRequired);
      const notes = asString(args.notes);
      if (firstName !== undefined) patch.firstName = firstName;
      if (lastName !== undefined) patch.lastName = lastName;
      if (phone !== undefined) patch.phone = phone;
      if (rsvpStatus !== undefined) patch.rsvpStatus = rsvpStatus;
      if (mealPreference !== undefined) patch.mealPreference = mealPreference;
      if (groupId !== undefined) patch.groupId = groupId;
      if (accommodationRequired !== undefined) patch.accommodationRequired = accommodationRequired;
      if (notes !== undefined) patch.notes = notes;
      if (Object.keys(patch).length === 0) {
        throw new Error("No fields to update were provided.");
      }
      await ctx.operations.guests.updateGuest(
        ctx.weddingId,
        guestId,
        patch,
        ctx.guests().map((g) => (g.id === guestId ? { ...g, ...patch } : g)),
      );
      return { ok: true, guestId, updated: Object.keys(patch) };
    },
  },
  {
    declaration: {
      name: "delete_guest",
      description:
        "Remove a guest from the list. This is a reversible soft delete. You MUST first call list_guests to obtain the exact guestId.",
      parameters: Schema.object({
        properties: {
          guestId: Schema.string({ description: "Exact guest ID from list_guests." }),
        },
      }),
    },
    destructive: true,
    describe: (args, ctx) => `Usunięcie gościa: ${guestName(ctx, asString(args.guestId) ?? "")}`,
    execute: async (args, ctx) => {
      if (ctx.isDemoAccount()) {
        throw new Error(ctx.translate("demo.guestDeleteBlocked"));
      }
      const guestId = requireString(args, "guestId");
      if (!ctx.guests().some((g) => g.id === guestId)) {
        throw new Error(`No guest with ID "${guestId}". Call list_guests first.`);
      }
      await ctx.operations.guests.softDeleteGuest(ctx.weddingId, guestId, ctx.guests());
      return { ok: true, guestId };
    },
  },
];
